// `isEmpty` / the delete loop at the end of `mapTempCriteriaToExtensionCriteria`
// (search.page.component.ng1.ts:703).
//
// ⚠️ THE CRITERIA OBJECT THE FORM EMITS HAS NO EMPTY KEYS AT ALL. The mapper builds a literal
// with all sixteen keys and then DELETES every one whose value is empty — so what leaves the
// form is exactly "what the user filled in", and `Object.keys` over it is a meaningful
// question. Everything downstream depends on that pruning:
//
//   - the left rail's "Filters used n/9" counts options whose keys are PRESENT
//     (`calculateExtensionCriteriaUsedOptions`), so an unpruned object marks all nine used;
//   - `isExtensionCriteriaValid` is `Object.keys(criteria).length > 0` after removing the
//     defaults, so an unpruned object is always valid and the empty-form guard never fires.
//
// Reading the count as "keys present" without also pruning — which is what the two halves of
// the rule look like separately — inverts it: the rail reads 9/9 on a blank form.

/**
 * `isEmpty`, and its three cases are not one test.
 *
 * ⚠️ AN EMPTY ARRAY AND AN EMPTY OBJECT ARE EMPTY; `0` AND `false` ARE NOT. Upstream checks
 * length for arrays, key count for objects, and `undefined || null` for everything else — so a
 * numeric criterion of 0, or a boolean of false, survives. A truthiness test drops both.
 */
export function isEmptyCriterion(value: unknown): boolean {
  if (Array.isArray(value)) return value.length === 0
  // `angular.isObject` is false for null, which the last line catches instead.
  if (typeof value === 'object' && value !== null) return Object.keys(value).length === 0
  return value === undefined || value === null
}

/** The delete loop: every empty key removed, the rest untouched. */
export function pruneEmptyCriteria(criteria: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const pruned: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(criteria)) {
    if (!isEmptyCriterion(value)) pruned[key] = value
  }
  return pruned
}
